import React, { useCallback, useEffect, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Page } from '@templates'
import { ApplicationState } from '@store/types'
import { GridStyled, Loading, Separator, ColorWrapper, TextParagraph } from '@atoms'
import { StepRefin } from '@utils/enums'
import { clearSimulacao, trackingRefin } from '@store/refinanciamento/simulacao/actions'
import { useTrackingRefin } from '@hooks'
import IcDocument from '@interco/icons/core/files/ic_document'
import IcInfoCircle from '@interco/icons/core/status/ic_info_circle'
import { Colors } from '@utils/Colors'
import { Button } from '@interco/inter-ui/components/Button'
import Tag from '@utils/Tag'
import routes from '@routes/routes'
import { BridgeService } from '@services/bridge'
import {
  marginAcceptRequest,
  resetMarginAcceptResult,
} from '@store/refinanciamento/marginAcceptResult/actions'
import {
  ConsultaMargemPayload,
  MarginAcceptApiErrorCodes,
} from '@store/refinanciamento/marginAcceptResult/types'
import { ContratoIndisponivel, ContratoDisponivel } from '@molecules'
import {
  getDadosPessoaisRequest,
  resetDadosPessoaisLoading,
} from '@store/refinanciamento/dadosPessoais/actions'
import { getNextRoute } from '@store/refinanciamento/dadosPessoais/utils'

import {
  dialogVariants,
  VariantDialogContratos,
} from './Bottomsheets/BottomsheetCustom/dialogVariants'
import { Grid, GridScroll, Line, ConvenioHeader, TutorialLink, TutorialLinkRow } from './styles'
import BottomsheetCustom from './Bottomsheets/BottomsheetCustom'
import BottomsheetConvenio from './Bottomsheets/BottomsheetConvenio'
import {
  isContratoElegivel,
  disableContinue,
  disabledCheckbox,
  verificarSelecaoPorMatricula,
  temContratosElegiveis,
  hasContratoInss,
  hasContratoCtps,
  hasContratoSelecaoUnica,
  createContratosHelpers,
  hasContratoSelecionado,
  isConvenioClt,
} from './helpers'

const Contratos = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [showBottomSheetConvenio, setShowBottomSheetConvenio] = useState(false)
  const [showBottomSheet, setShowBottomSheet] = useState(false)
  const [variantDialog, setVariantDialog] = useState<VariantDialogContratos>(
    dialogVariants.SaibaMais,
  )
  const [isContinueLoading, setIsContinueLoading] = useState(false)

  const {
    listaContratos,
    matriculaSelecionada,
    contratosSelecionados,
    convenioCodigo,
    convenioName,
  } = useSelector((state: ApplicationState) => state.refinanciamento.contratos)
  const { isApp } = useSelector((state: ApplicationState) => state.ui.navigation)
  const { loading: loadingMarginAccept } = useSelector(
    (state: ApplicationState) => state.refinanciamento.marginAcceptResult,
  )
  const { loading: loadingCustomerData } = useSelector(
    (state: ApplicationState) => state.refinanciamento.dadosPessoais,
  )
  const isLoading = isContinueLoading || Boolean(loadingMarginAccept || loadingCustomerData)

  const { handleContratoChange, handleSelecionarTodos, handleContratoClick } =
    createContratosHelpers({
      dispatch,
      contratosSelecionados,
      matriculaSelecionada,
      convenioCodigo,
      isApp,
      setVariantDialog,
      setShowBottomSheet,
    })

  useTrackingRefin(StepRefin.CONTRATOS)

  const handleTutorialClick = useCallback(() => {
    navigate(routes.TUTORIAL, { state: { from: routes.CONTRATOS } })
  }, [navigate])

  useEffect(() => {
    BridgeService.tac({
      name: 'contratos',
      actions: ['buscar_contratos'],
    })
  }, [])

  useEffect(() => {
    dispatch(resetMarginAcceptResult())
    dispatch(resetDadosPessoaisLoading())
    dispatch(clearSimulacao())
  }, [dispatch])

  useEffect(() => {
    if (loadingMarginAccept || loadingCustomerData) return

    setIsContinueLoading(false)
  }, [loadingMarginAccept, loadingCustomerData])

  useEffect(() => {
    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:selection',
        ref_type: '',
        content_action: 'Dado carregado',
        action_id: 'Selecione os contratos para refinanciar',
        section: 'selection',
        sub_section: '',
        step: '',
        scenario: 'Seleção de contratos',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'page_loaded',
        element_name: 'Selecione os contratos para refinanciar',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:contracts',
        section: 'contracts',
        sub_section: '',
        step: '',
        scenario: 'Seleção de contratos',
      })
    }
  }, [convenioCodigo, isApp])

  return (
    <Page
      positionFooter="sticky"
      stickyContainerFooterBottom="0"
      stickyFooter={
        <>
          <Button
            fullWidth
            disabled={disableContinue(contratosSelecionados) || isLoading}
            isLoading={isLoading}
            variant="primary"
            onClick={() => {
              dispatch(trackingRefin(StepRefin.CLICK_CONTRATOS_CONTINUAR))
              if (isApp) {
                Tag.enviarTagueamentoApp({
                  screen: 'clt:refin:selection',
                  ref_type: 'botao',
                  content_action: 'Toque',
                  action_id: 'Continuar',
                  section: 'selection',
                  sub_section: '',
                  step: '',
                  scenario: 'Seleção de contratos',
                  ref_figma: '2',
                })
              } else {
                Tag.enviarTagueamentoWeb({
                  element_action: 'click button',
                  element_name: 'Continuar',
                  c_page: window.location.href,
                  redirect_url: '',
                  section_name: 'payrollloan:refin:contracts',
                  section: 'contracts',
                  sub_section: '',
                  step: '',
                  scenario: 'Seleção de contratos',
                })
              }

              if (!convenioName) {
                BridgeService.tic({
                  name: 'btn_continuar',
                  action: 'continuar_refinanciamento_sem_convenio',
                })
                navigate(routes.ERRO, {
                  state: {
                    convenioNameLocation: convenioName,
                    step: 'convenio-indisponivel',
                  },
                })
                return
              }

              if (
                hasContratoInss(contratosSelecionados) ||
                hasContratoCtps(contratosSelecionados)
              ) {
                BridgeService.tic({
                  name: 'btn_continuar',
                  action: `continuar_refinanciamento_${convenioName?.toLowerCase()}`,
                })
                setShowBottomSheetConvenio(true)
              } else {
                BridgeService.tic({
                  name: 'btn_continuar',
                  action: `continuar_refinanciamento_${convenioName?.toLowerCase()}`,
                })

                setIsContinueLoading(true)

                const handleMarginAcceptError = (errorCode?: string) => {
                  if (errorCode === MarginAcceptApiErrorCodes.PROCESSAMENTO_VIRADA_COMPETENCIA) {
                    navigate(routes.SERVICO_INDISPONIVEL)
                  }
                }

                if (isApp) {
                  dispatch(
                    marginAcceptRequest({
                      callback: () => {
                        navigate(routes.CARREGAMENTO_SIMULACAO)
                      },
                      errorCallback: handleMarginAcceptError,
                      selectedContracts: contratosSelecionados?.map((c) => c.numeroContrato) || [],
                      convenioName,
                    } as ConsultaMargemPayload),
                  )
                } else {
                  dispatch(
                    marginAcceptRequest({
                      callback: () => {
                        dispatch(
                          getDadosPessoaisRequest({
                            callback: () => {
                              navigate(getNextRoute())
                            },
                          }),
                        )
                      },
                      errorCallback: handleMarginAcceptError,
                      selectedContracts: contratosSelecionados?.map((c) => c.numeroContrato) || [],
                      convenioName,
                    } as ConsultaMargemPayload),
                  )
                }
              }
            }}
          >
            Continuar
          </Button>
          <div />
        </>
      }
    >
      <TextParagraph variant="headline-h2" fontWeight="600" margin="0 0 8px 0">
        Selecione os contratos que deseja refinanciar
      </TextParagraph>

      <TextParagraph variant="body-3">
        É possível selecionar vários contratos do mesmo convênio, matrícula e ou benefício
      </TextParagraph>

      <Separator margin="24px -24px" height="1px" />
      <Grid>
        {listaContratos?.length ? (
          listaContratos?.map((contratosAtivos) => (
            <GridScroll key={contratosAtivos.descricaoConvenio}>
              <ConvenioHeader>
                <IcDocument
                  width={24}
                  height={24}
                  color={Colors.GRAY500}
                  style={{ marginRight: '4px' }}
                />
                <TextParagraph variant="headline-h3" margin="8px 0 8px 0" fontWeight="600">
                  {contratosAtivos.descricaoConvenio}
                </TextParagraph>
              </ConvenioHeader>

              {contratosAtivos.listMatricula.map((listaMatriculas, matriculaIndex) => (
                <Grid key={listaMatriculas.numeroMatricula}>
                  {matriculaIndex !== 0 && (
                    <Separator variant="small" margin="24px -24px" height="1px" />
                  )}
                  <Line>
                    <TextParagraph variant="body-3" fontWeight="700">
                      {`${
                        contratosAtivos.descricaoConvenio === 'INSS' ? 'Benefício:' : 'Matrícula:'
                      } ${listaMatriculas.numeroMatricula}`}
                    </TextParagraph>
                    <ColorWrapper
                      color={
                        !disabledCheckbox(
                          listaMatriculas.numeroMatricula,
                          listaMatriculas.listContrato[0],
                          matriculaSelecionada,
                          contratosSelecionados,
                        ) && temContratosElegiveis(listaMatriculas)
                          ? Colors.PRIMARY500
                          : Colors.GRAY300
                      }
                      onClick={() => {
                        if (
                          disabledCheckbox(
                            listaMatriculas.numeroMatricula,
                            listaMatriculas.listContrato[0],
                            matriculaSelecionada,
                            contratosSelecionados,
                          )
                        ) {
                          setVariantDialog(
                            hasContratoSelecaoUnica(contratosSelecionados)
                              ? dialogVariants.SelecaoUnica
                              : dialogVariants.SelecaoContratos,
                          )
                          setShowBottomSheet(true)
                        } else if (temContratosElegiveis(listaMatriculas)) {
                          handleSelecionarTodos(listaMatriculas)
                        }
                      }}
                    >
                      <TextParagraph variant="body-3" fontWeight="700">
                        {verificarSelecaoPorMatricula(listaMatriculas, contratosSelecionados) &&
                        temContratosElegiveis(listaMatriculas)
                          ? 'Excluir seleção'
                          : 'Selecionar todos'}
                      </TextParagraph>
                    </ColorWrapper>
                  </Line>

                  {listaMatriculas.listContrato.map((contrato, contratoIndex) => {
                    const isDisabled = disabledCheckbox(
                      listaMatriculas.numeroMatricula,
                      contrato,
                      matriculaSelecionada,
                      contratosSelecionados,
                    )
                    const isDisabledByParcelas = Boolean(contrato.indisponivel)
                    const checked = Boolean(
                      hasContratoSelecionado(contrato.numeroContrato, contratosSelecionados),
                    )

                    if (!isContratoElegivel(contrato)) {
                      return (
                        <ContratoIndisponivel
                          key={contrato.numeroContrato}
                          contrato={contrato}
                          contratoIndex={contratoIndex}
                          onClick={() =>
                            handleContratoClick(listaMatriculas.numeroMatricula, contrato)
                          }
                          isDisabled={isDisabled}
                        />
                      )
                    }
                    return (
                      <ContratoDisponivel
                        key={contrato.numeroContrato}
                        contrato={contrato}
                        contratoIndex={contratoIndex}
                        isDisabled={isDisabled}
                        isDisabledByParcelas={isDisabledByParcelas}
                        checked={checked}
                        onClick={() =>
                          handleContratoClick(listaMatriculas.numeroMatricula, contrato)
                        }
                        onChange={() =>
                          handleContratoChange(contrato, listaMatriculas.numeroMatricula)
                        }
                      />
                    )
                  })}

                  {isConvenioClt(contratosAtivos.descricaoConvenio) && matriculaIndex === 0 && (
                    <TutorialLinkRow>
                      <IcInfoCircle width={16} height={16} color={Colors.PRIMARY500} />
                      <TutorialLink type="button" onClick={handleTutorialClick}>
                        Saiba mais sobre o refinanciamento
                      </TutorialLink>
                    </TutorialLinkRow>
                  )}
                </Grid>
              ))}
            </GridScroll>
          ))
        ) : (
          <GridStyled>
            <Loading />
          </GridStyled>
        )}
      </Grid>
      <>
        {showBottomSheet && (
          <BottomsheetCustom
            setShowBottomSheet={setShowBottomSheet}
            variant={variantDialog}
            customTextButton="Entendi"
          />
        )}
      </>
      {showBottomSheetConvenio ? (
        <BottomsheetConvenio show={showBottomSheetConvenio} setShow={setShowBottomSheetConvenio} />
      ) : (
        <></>
      )}
    </Page>
  )
}

export default Contratos
